import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import "./Lightbox.scss";

const Lightbox = ({ images, currentIndex, setIndex, onClose }) => {
    const isOpen = currentIndex !== null;

    // Stable callbacks (remove re-renders + fix useEffect warning)
    const showPrev = useCallback(
        (e) => {
            e?.stopPropagation();
            setIndex((i) => (i > 0 ? i - 1 : images.length - 1));
        },
        [setIndex, images.length]
    );

    const showNext = useCallback(
        (e) => {
            e?.stopPropagation();
            setIndex((i) => (i < images.length - 1 ? i + 1 : 0));
        },
        [setIndex, images.length]
    );

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKey = (e) => {
            if (e.key === "Escape") handleClose();
            if (e.key === "ArrowLeft") showPrev(e);
            if (e.key === "ArrowRight") showNext(e);
        };

        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);

    }, [isOpen, handleClose, showPrev, showNext]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="lightbox-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleClose}
                >
                    <button className="lightbox-close" onClick={handleClose}>
                        <X size={32} />
                    </button>

                    <button className="lightbox-prev" onClick={showPrev}>
                        <ChevronLeft size={48} />
                    </button>

                    <button className="lightbox-next" onClick={showNext}>
                        <ChevronRight size={48} />
                    </button>

                    <motion.img
                        key={currentIndex}
                        src={images[currentIndex]}
                        className="lightbox-image"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Lightbox;
